import { Children, useEffect, useState, useRef } from "react";
import { Link, Switch, Route, Redirect, useHistory, useLocation } from "react-router-dom";

/**
 *  Tab component based on Material UI Tabs
 *
 * @param {React.FunctionComponent} props
 * @returns {JSX.Element}
 */
export function RouteTabs({ children, onTabChange }) {
	const [currendIndex, setCurrentIndex] = useState(0);
	const [tabBarStyle, setTabBarStyle] = useState({});
	const history = useHistory();
	const location = useLocation();
	const tabsRef = useRef([]);

	if (children && children.length > 1) {
		Children.forEach(children, child => {
			if (child && child.type?.name !== RouteTab.name) {
				throw Error("Tabs must only have `Tab` children");
			}
		});
	} else {
		throw Error("Tab must have atleast 2 Tab");
	}

	const setActiveTab = index => {
		setCurrentIndex(index);
		if (onTabChange) {
			onTabChange(index);
		}
		if (children[index]?.props?.onClick) {
			children[index].props.onClick(index);
		}
	};

	useEffect(() => {
		Children.forEach(children, (child, index) => {
			if (child?.props?.to === history.location.pathname) {
				setCurrentIndex(index);
			}
		});
	}, [location]);

	useEffect(() => {
		if (tabsRef.current[currendIndex]) {
			const activeTab = tabsRef.current[currendIndex];
			setTabBarStyle({
				width: `${activeTab.offsetWidth}px`,
				left: `${activeTab.offsetLeft}px`
			});
		}
	}, [currendIndex]);

	const tabTitles = Children.map(children, (child, index) => {
		if (child) {
			return (
				<a
					ref={el => (tabsRef.current[index] = el)}
					style={{ cursor: child?.props?.disabled ? "not-allowed" : "pointer" }}
					className={`konf-tab-title  ${currendIndex == index ? "active" : ""}`}
					key={index}
					disabled={child?.props?.disabled}
					onClick={async () => {
						if (!child?.props?.disabled) history.push(child?.props?.to);
					}}
				>
					{child?.props?.title}
					{child?.props?.icon && <img src={child.props.icon} className="tab-icon ml-3" />}
				</a>
			);
		}
	});

	return (
		<>
			<div className="konf-tabs-wrapper">
				<div className="konf-tabs">
					<div className="tabs-container">
						{tabTitles}
						<span
							className="tab-bar"
							style={tabBarStyle}
						></span>
					</div>
				</div>
			</div>
			<Switch>
				{children.map((child, index) => {
					if (child)
						return (
							<Route
								exact={child.props.exact === undefined ? true : child.props.exact}
								key={index}
								path={child.props.to}
							>
								{child}
							</Route>
						);
				})}
				<Redirect to="/error" />
			</Switch>
			<style>
				{`
				.konf-tabs-wrapper {
					position: relative;
					width: 100%;
					overflow-x: auto;
					-webkit-overflow-scrolling: touch;
					scrollbar-width: none; /* Firefox */
					-ms-overflow-style: none;  /* IE and Edge */
				}
				.konf-tabs-wrapper::-webkit-scrollbar {
					display: none; /* Chrome, Safari and Opera */
				}
				.konf-tabs {
					position: relative;
					width: 100%;
					border-bottom: 1px solid #e0e0e050;
					scrollbar-width: none;
					-ms-overflow-style: none;
				}
				.konf-tabs::-webkit-scrollbar {
					display: none;
				}
				.tabs-container {
					display: inline-flex;
					min-width: 100%;
					position: relative;
					scrollbar-width: none;
					-ms-overflow-style: none;
				}
				.tabs-container::-webkit-scrollbar {
					display: none;
				}
				.konf-tabs a {
					flex: 1;
					position: relative;
					text-decoration: none;
					padding: 15px;
					text-transform: uppercase;
					color: var(--fourthColor);
					background-color: white;
					text-align: center;
					vertical-align: middle;
					outline: none;
					border: none;
					white-space: nowrap;
					min-width: fit-content;
				}
				.konf-tabs a:not(.active):not([disabled]):hover {
					color: var(--primaryColor);
				}
				.konf-tabs a:not(.active):disabled {
					color: #d1d1d1;
				}
				.konf-tabs a:not(.active):active {
					background-color: #c3c3c375;
				}
				a.active {
					color: var(--primaryColor);
				}
				.tab-bar {
					position: absolute;
					z-index: 10;
					bottom: 0;
					height: 2px;
					background: var(--primaryColor);
					display: block;
					transition: all .2s ease;
					-webkit-transition: all .2s ease;
				}

				@media screen and (max-width: 768px) {
					.konf-tabs a {
						padding: 15px 10px;
					}
				}
				`}
			</style>
		</>
	);
}

export function RouteTab(props) {
	if (!props.title) {
		throw Error("Tab must have title");
	} else if (!props.to) {
		throw Error("Tab must contain `to` prop");
	}
	return props.children;
}

RouteTab.defaultProps = {
	disabled: false
};
